import { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, query, orderBy, where, getDocs, limit, startAfter } from 'firebase/firestore';

const PAGE_SIZE = 30;

const actionLabels = {
  'product.created': 'Producto creado',
  'product.updated': 'Producto editado',
  'product.deleted': 'Producto eliminado',
  'product.price.changed': 'Precio cambiado',
  'provider.created': 'Proveedor creado',
  'provider.updated': 'Proveedor actualizado',
  'provider.deleted': 'Proveedor eliminado',
  'incoming.created': 'Entrada creada',
  'incoming.updated': 'Entrada editada',
  'incoming.deleted': 'Entrada eliminada',
  'incoming.paymentStatus.toggled': 'Estado de pago cambiado',
  'outgoing.created': 'Salida creada',
  'outgoing.updated': 'Salida editada',
  'outgoing.deleted': 'Salida eliminada',
  'expense.created': 'Gasto registrado',
  'expense.updated': 'Gasto editado',
  'expense.deleted': 'Gasto eliminado',
  'sale.created': 'Venta registrada',
  'sale.voided': 'Venta anulada',
  'sale.refund': 'Devolucion registrada',
  'cash.opened': 'Caja abierta',
  'cash.closed': 'Caja cerrada',
  'cash.discrepancy': 'Diferencia de caja',
  'user.created': 'Usuario creado',
  'user.updated': 'Usuario editado',
  'user.activated': 'Usuario activado',
  'user.deactivated': 'Usuario desactivado',
  'user.reset.sent': 'Reset de clave enviado',
  'payroll.settings.saved': 'Configuracion de nomina guardada',
  'payroll.receipt.generated': 'Comprobante de nomina generado',
};

const keyMap = {
  name: 'Nombre',
  productName: 'Producto',
  category: 'Categoria',
  barcode: 'Codigo de barras',
  invoice: 'Factura',
  supplier: 'Proveedor',
  quantity: 'Cantidad',
  reason: 'Motivo',
  newStock: 'Stock nuevo',
  productId: 'ID',
};

const formatValue = (v) => {
  if (v === null || v === undefined) return '-';
  if (v?.toDate) return v.toDate().toLocaleString();
  if (v instanceof Date) return v.toLocaleString();
  if (typeof v === 'boolean') return v ? 'Si' : 'No';
  return String(v);
};

const flattenDetails = (obj, parent = '') => {
  if (!obj || typeof obj !== 'object') return [];
  const entries = [];
  for (const [k, v] of Object.entries(obj)) {
    const label = parent ? `${parent}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v) && !v.toDate) {
      entries.push(...flattenDetails(v, label));
    } else if (Array.isArray(v)) {
      entries.push({ key: label, value: v.map(formatValue).join(', ') });
    } else {
      entries.push({ key: label, value: formatValue(v) });
    }
  }
  return entries;
};

const Severity = ({ sev = 'info' }) => {
  const map = { info: 'bg-blue-100 text-blue-800', warning: 'bg-amber-100 text-amber-800', warn: 'bg-amber-100 text-amber-800', critical: 'bg-red-100 text-red-700' };
  const label = { info: 'info', warning: 'advertencia', warn: 'advertencia', critical: 'critica' }[sev] || sev;
  const cls = map[sev] || map.info;
  return <span className={`text-xs px-2 py-1 rounded-full ${cls}`}>{label}</span>;
};

const AuditLogView = ({ onBack }) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [cursors, setCursors] = useState([]);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', userEmail: 'all', action: 'all' });
  const [facetUsers, setFacetUsers] = useState([]);
  const [facetActions, setFacetActions] = useState(Object.keys(actionLabels));
  const [detailModal, setDetailModal] = useState({ open: false, log: null, flat: [] });

  const toDate = (v) => (v ? new Date(v + 'T00:00:00') : null);

  const buildQuery = async (cursor) => {
    let qRef = query(collection(db, 'auditLogs'), orderBy('createdAt', 'desc'));
    const s = toDate(filters.startDate);
    const e = toDate(filters.endDate);
    if (s) qRef = query(qRef, where('createdAt', '>=', s));
    if (e) {
      const ee = new Date(e.getTime());
      ee.setHours(23, 59, 59, 999);
      qRef = query(qRef, where('createdAt', '<=', ee));
    }
    if (filters.action && filters.action !== 'all') qRef = query(qRef, where('action', '==', filters.action));
    // Usuario: muchos logs no traen userEmail, por eso filtramos en memoria si se selecciona usuario
    const baseLimit = filters.userEmail !== 'all' ? 200 : PAGE_SIZE;
    qRef = query(qRef, limit(baseLimit));
    if (cursor) qRef = query(qRef, startAfter(cursor));
    const snap = await getDocs(qRef);
    let data = snap.docs.map((d) => ({ id: d.id, ...d.data(), _doc: d }));
    if (filters.userEmail && filters.userEmail !== 'all') {
      const sel = String(filters.userEmail).toLowerCase();
      data = data.filter((x) => {
        const a = (x.userEmail || '').toLowerCase();
        const b = (x.user?.email || '').toLowerCase();
        return a === sel || b === sel;
      });
    }
    return data.slice(0, PAGE_SIZE); // mantener paginacion visible
  };

  const loadPage = async (goToPage = 1) => {
    try {
      let cursor = null;
      if (goToPage > 1 && cursors[goToPage - 2]) cursor = cursors[goToPage - 2];
      const data = await buildQuery(cursor);
      setRows(data);
      setPage(goToPage);
      if (data.length > 0) {
        const last = data[data.length - 1]._doc;
        const nextCursors = [...cursors];
        nextCursors[goToPage - 1] = last;
        setCursors(nextCursors);
      }
    } catch (e) {
      console.error('audit load error', e);
      setRows([]);
    }
  };

  useEffect(() => { loadPage(1); }, []);
  useEffect(() => { setCursors([]); loadPage(1); }, [filters.startDate, filters.endDate, filters.userEmail, filters.action]);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'auditLogs'), orderBy('createdAt', 'desc'), limit(200)));
        const emails = new Set();
        const actions = new Set(Object.keys(actionLabels));
        snap.docs.forEach((d) => {
          const x = d.data() || {};
          const em = (x.userEmail || x?.user?.email || '').toString().toLowerCase();
          if (em) emails.add(em);
          if (x?.action) actions.add(String(x.action));
        });
        setFacetUsers(Array.from(emails).sort());
        setFacetActions(Array.from(actions).sort());
      } catch (e) {
        setFacetUsers([]);
        setFacetActions(Object.keys(actionLabels));
      }
    })();
    const unsub = (collection(db, 'users'), (ss) => {
      try {
        const emails = ss.docs.map((d) => (d.data()?.email || '')).filter(Boolean);
        setFacetUsers((prev) => Array.from(new Set([...(prev || []), ...emails])).sort());
      } catch {}
    });
    return () => unsub();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fmtTS = (v) => {
    if (!v) return '';
    if (v?.toDate) try { return v.toDate().toLocaleString(); } catch { return ''; }
    if (v instanceof Date) return v.toLocaleString();
    return String(v);
  };

  const openDetails = (log) => {
    const flat = flattenDetails(log.details || {});
    setDetailModal({ open: true, log, flat });
  };
  const closeDetails = () => setDetailModal({ open: false, log: null, flat: [] });

  const truncatedDetails = (log) => {
    const flat = flattenDetails(log.details || {});
    if (!flat.length) return '-';
    const preview = flat.slice(0, 2).map(({ key, value }) => `${keyMap[key] || key}: ${value}`).join(' · ');
    return preview.length > 80 ? preview.slice(0, 80) + '…' : preview;
  };

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400"
        >
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menu
        </button>
        <h1 className="text-4xl font-extrabold text-gray-900 drop-shadow-sm">Bitacora de Auditoria</h1>
        <div className="w-32"></div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Filtros de Auditoria</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
            <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
            <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <select name="userEmail" value={filters.userEmail} onChange={handleFilterChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
              <option value="all">Todos</option>
              {facetUsers.map((user) => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Accion</label>
            <select name="action" value={filters.action} onChange={handleFilterChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
              <option value="all">Todas</option>
              {facetActions.map((action) => (
                <option key={action} value={action}>{actionLabels[action] || action}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={() => loadPage(1)} className="px-4 py-2 border rounded hover:bg-gray-50">Refrescar</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha y Hora</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Accion</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detalles</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.length > 0 ? (
                rows.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{fmtTS(log.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log?.userEmail || log?.user?.email || log?.user?.name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center gap-2">
                      <span>{actionLabels[log.action] || log.action}</span>
                      <Severity sev={log.severity} />
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-600">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-700 truncate max-w-xs" title={truncatedDetails(log)}>
                          {truncatedDetails(log)}
                        </span>
                        <button
                          onClick={() => openDetails(log)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
                        >
                          Ver
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No hay registros de auditoria con los filtros seleccionados.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t">
          <button onClick={() => page > 1 && loadPage(page - 1)} className="px-3 py-2 border rounded disabled:opacity-50" disabled={page <= 1}>Pagina anterior</button>
          <div className="text-sm text-gray-600">Pagina {page}</div>
          <button onClick={() => rows.length === PAGE_SIZE && loadPage(page + 1)} className="px-3 py-2 border rounded disabled:opacity-50" disabled={rows.length < PAGE_SIZE}>Pagina siguiente</button>
        </div>
      </div>

      {detailModal.open && detailModal.log && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 p-6 relative">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Detalle de auditoria</h3>
                <p className="text-sm text-gray-600">{fmtTS(detailModal.log.createdAt)} · {actionLabels[detailModal.log.action] || detailModal.log.action}</p>
              </div>
              <button onClick={closeDetails} className="text-gray-500 hover:text-gray-800 text-lg">✕</button>
            </div>
            <div className="mb-4 text-sm text-gray-700">
              <div className="mb-1"><strong>Usuario:</strong> {detailModal.log?.userEmail || detailModal.log?.user?.email || detailModal.log?.user?.name || '-'}</div>
              <div className="mb-1"><strong>Accion:</strong> {actionLabels[detailModal.log.action] || detailModal.log.action}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 max-h-96 overflow-auto text-sm text-gray-800 pr-1">
              {(detailModal.flat.length ? detailModal.flat : [{ key: 'detalle', value: formatValue(detailModal.log.details) }]).map((item, idx) => (
                <div key={idx} className="flex flex-col">
                  <span className="font-semibold text-gray-700">{keyMap[item.key] || item.key}:</span>
                  <span className="text-gray-800 break-words whitespace-pre-wrap">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <button onClick={() => { navigator.clipboard?.writeText(JSON.stringify(detailModal.log.details || {}, null, 2)); }} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Copiar</button>
              <button onClick={closeDetails} className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogView;
